import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";

import DatagridTooltipProvider from "./datagridTooltipProvider.js";
import { useEffect, useMemo, useRef, useState } from "react";

import { Refresh } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import {
  createOmniPaginationObject,
  mrtGetBodyRowSx,
  mrtGetClampTextSx,
  mrtGetDateColumnSizing,
  mrtGetTableWrapperSx,
} from "../../utils/helpers";
import MRTDateTimeCell from "./mrtDateTimeCell";

const formatCellValue = value => {
  if (value == null) {
    return "";
  }

  if (value === "") {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (Array.isArray(value)) {
    return value
      .map(item => formatCellValue(item))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object") {
    if (Object.keys(value).length === 0) {
      return "";
    }

    if (value.label || value.url) {
      return [value.label, value.url].filter(Boolean).join(" - ");
    }

    return Object.values(value)
      .map(item => formatCellValue(item))
      .filter(Boolean)
      .join(", ");
  }

  return value;
};

const getTextCellProps = theme => {
  return {
    sx: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
      padding: "4px 3px",
      verticalAlign: "top",
    },
  };
};

const getCenteredCellProps = theme => {
  return {
    align: "center",
    sx: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
      padding: "4px 3px",
      verticalAlign: "top",
    },
  };
};

const renderTextCell = cell => {
  return (
    <Typography
      variant="body2"
      sx={{ ...mrtGetClampTextSx(), fontSize: "0.74rem", lineHeight: 1.1 }}
    >
      {formatCellValue(cell.getValue())}
    </Typography>
  );
};

const renderCenteredTextCell = cell => {
  return (
    <Typography
      variant="subtitle1"
      sx={{
        textAlign: "center",
        fontSize: "0.76rem",
        lineHeight: 1.1,
      }}
    >
      {formatCellValue(cell.getValue())}
    </Typography>
  );
};

const calculateColumns = theme => {
  return [
    {
      accessorKey: "updatedAt",
      header: "Updated",
      ...mrtGetDateColumnSizing(),
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: MRTDateTimeCell,
    },
    {
      accessorKey: "displayName",
      header: "Display Name",
      size: 140,
      minSize: 110,
      maxSize: 200,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "ConversationSummary",
      header: "Summary of Conversations",
      size: 280,
      minSize: 180,
      maxSize: 360,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "NextBestAction",
      header: "Next Best Action",
      size: 240,
      minSize: 160,
      maxSize: 320,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    // ---- Columns below are hidden by default but kept available via the
    // ---- show/hide-columns button.
    {
      accessorKey: "createdAt",
      header: "Created",
      ...mrtGetDateColumnSizing(),
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: MRTDateTimeCell,
    },
    {
      accessorKey: "memberId",
      header: "Member ID",
      size: 140,
      maxSize: 200,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "lastProductShown",
      header: "Last Product Shown",
      size: 160,
      minSize: 120,
      maxSize: 240,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "Archived",
      header: "Archived",
      size: 90,
      maxSize: 110,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderCenteredTextCell(cell),
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnOrder: [
      "updatedAt",
      "displayName",
      "ConversationSummary",
      "NextBestAction",
      "createdAt",
      "memberId",
      "lastProductShown",
      "Archived",
    ],
    columnVisibility: {
      updatedAt: true,
      displayName: true,
      ConversationSummary: true,
      NextBestAction: true,
      createdAt: false,
      memberId: false,
      lastProductShown: false,
      Archived: false,
    },
  };
};

export const BotpressMembersTable = props => {
  const {
    data,
    onRequestNewPage = null,
    dataReduxPagination = null,
    totalItemCount = 0,
    onRefreshTable = null,
  } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null);

  const columns = useMemo(() => calculateColumns(theme), [theme]);
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(totalItemCount);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshIconSpinning, setIsRefreshIconSpinning] = useState(false);

  // Default ordering comes from createdAt (newest first). In manualSorting mode
  // this id is forwarded to the backend query even when the column is hidden.
  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [columnOrder, setColumnOrder] = useState(initialState.columnOrder);

  useEffect(() => {
    setRowCount(totalItemCount);
  }, [totalItemCount]);

  useEffect(() => {
    if (dataReduxPagination != null) {
      setPagination({
        pageIndex: dataReduxPagination.pageIndex,
        pageSize: dataReduxPagination.pageSize,
      });
      setRowCount(dataReduxPagination.totalItems);
    }
  }, [dataReduxPagination]);

  useEffect(() => {
    setIsLoading(false);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      if (!onRequestNewPage) {
        return;
      }

      if (!data.length) {
        setIsLoading(true);
      }

      try {
        let newQuery = {};

        columnFilters.forEach(element => {
          if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject(dataReduxPagination);
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        onRequestNewPage(newPage);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        return;
      }
    };

    fetchData();
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnResizing: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: false,
    enableRowSelection: false,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    rowCount,
    getRowId: row => row.id,
    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      minSize: 60,
      maxSize: 220,
      size: 100,
      grow: false,
    },
    state: {
      columnFilters,
      isLoading,
      pagination,
      sorting,
      columnOrder,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    paginationDisplayMode: "pages",

    enableFirstPageButton: false,
    enableLastPageButton: false,

    manualPagination: true,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },

    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        flexShrink: 0,
        minHeight: "48px",
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        flexShrink: 0,
        height: "56px",
        minHeight: "56px",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: mobileLayout
        ? {
            flex: 1,
            minHeight: 0,
            maxHeight: "none",
            overflowX: "auto",
            overflowY: "auto",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            flex: 1,
            minHeight: 0,
            maxHeight: "none",
            overflowX: "auto",
            overflowY: "auto",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
        tableLayout: "fixed",
        width: "100%",
      },
    },
    muiTableBodyRowProps: {
      sx: mrtGetBodyRowSx(),
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: alpha(theme.palette.commonground.contrastText, 0.12),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
        fontWeight: "bold",
        fontSize: "0.72rem",
        padding: "3px 3px",
        whiteSpace: "nowrap",
      },
    },
    renderTopToolbarCustomActions: () => <Box sx={{ minHeight: "40px" }} />,
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}

        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        {onRefreshTable && (
          <Tooltip title="Refresh members" arrow>
            <IconButton
              onClick={() => {
                setIsRefreshIconSpinning(true);
                onRefreshTable();

                setTimeout(() => {
                  setIsRefreshIconSpinning(false);
                }, 800);
              }}
            >
              <Refresh
                sx={{
                  transformOrigin: "center",
                  animation: isRefreshIconSpinning ? "refreshSpin 0.8s linear 1" : "none",
                  "@keyframes refreshSpin": {
                    from: {
                      transform: "rotate(0deg)",
                    },
                    to: {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
  });

  return (
    <Box sx={mrtGetTableWrapperSx(0, !data?.length)}>
      <DatagridTooltipProvider>
        <MaterialReactTable table={table} />
      </DatagridTooltipProvider>
    </Box>
  );
};

export default BotpressMembersTable;
